import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const formatCurrency = (value) => 
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

export default function Receivables(props) {
  let receivablesData = props.data || [];

  // Dynamically calculate chart width: 80px per customer + 100px padding for labels
  // If the list is short, it defaults to 100% of the screen width
  const calculatedWidth = Math.max(800, (receivablesData.length * 80) + 100);

  return (
    <div style={{ 
      padding: '4px', 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', 
      margin: '0 auto',
      backgroundColor: '#f8fafc',
      color: '#334155',
      borderRadius: '16px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.03)'
    }}>
     
      {/* 1. DATA TABLE SECTION */}
      <div style={{ 
        overflowX: 'auto', 
        marginBottom: '40px', 
        backgroundColor: '#ffffff', 
        border: '1px solid #e2e8f0', 
        borderRadius: '12px',
        maxHeight:'300px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.02)'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f1f5f9' }}>
              <th style={{ padding: '16px', fontWeight: '600', color: '#475569', fontSize: '12px', letterSpacing: '0.05em' }}>Customer Name</th>
              <th style={{ padding: '16px', fontWeight: '600', color: '#475569', fontSize: '12px', letterSpacing: '0.05em' }}>0 - 30 Days</th>
              <th style={{ padding: '16px', fontWeight: '600', color: '#475569', fontSize: '12px', letterSpacing: '0.05em' }}>31 - 60 Days</th>
              <th style={{ padding: '16px', fontWeight: '600', color: '#475569', fontSize: '12px', letterSpacing: '0.05em' }}>61 - 90 Days</th>
              <th style={{ padding: '16px', fontWeight: '600', color: '#475569', fontSize: '12px', letterSpacing: '0.05em' }}>91 - 120 Days</th>
              <th style={{ padding: '16px', fontWeight: '600', color: '#b91c1c', fontSize: '12px', letterSpacing: '0.05em' }}>Over 120 Days</th>
              <th style={{ padding: '16px', fontWeight: '600', color: '#0f172a', fontSize: '12px', letterSpacing: '0.05em' }}>Total Amount</th>
              <th style={{ padding: '16px', fontWeight: '600', color: '#64748b', fontSize: '12px', letterSpacing: '0.05em' }}>Sales Rep</th>
            </tr>
          </thead>
          <tbody>
            {receivablesData.map((row) => (
              <tr 
                key={row.CustomerName} 
                style={{ 
                  borderBottom: '1px solid #e2e8f0', 
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <td style={{ padding: '16px', fontWeight: '600', color: '#0f172a', maxWidth: '240px' }}>{row.CustomerName}</td>
                <td style={{ padding: '16px', color: row.Age_0_30 > 0 ? '#334155' : '#94a3b8' }}>{row.Age_0_30}</td>
                <td style={{ padding: '16px', color: row.Age_31_60 > 0 ? '#334155' : '#94a3b8' }}>{row.Age_31_60}</td>
                <td style={{ padding: '16px', color: row.Age_61_90 > 0 ? '#334155' : '#94a3b8' }}>{row.Age_61_90}</td>
                <td style={{ padding: '16px', color: row.Age_91_120 > 0 ? '#334155' : '#94a3b8' }}>{row.Age_91_120}</td>
                <td style={{ padding: '16px', color: '#dc2626', fontWeight: row.Age_Above120 > 0 ? '600' : '400' }}>{row.Age_Above120}</td>
                <td style={{ padding: '16px', color: '#0f172a', fontWeight: '700' }}>{row.Total_Amount}</td>
                <td style={{ padding: '16px', color: '#64748b', fontSize: '13px' }}>{row.Sales_Person}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 2. GRAPH SECTION */}
      <div style={{ 
        backgroundColor: '#ffffff', 
        padding: '24px', 
        border: '1px solid #e2e8f0', 
        borderRadius: '12px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.02)',
        overflowX: 'auto' // Enables horizontal scrolling for the chart card
      }}>
        {/* We use a fixed height of 400px, but width expands dynamically with the list size */}
        <div style={{ width: receivablesData.length > 8 ? calculatedWidth : '100%', height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={receivablesData}
              layout="horizontal" // Switched layout to horizontal
              margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
            >
              {/* Premium Light Modern Gradients */}
              <defs>
                <linearGradient id="ageCurrent" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#cbd5e1" /><stop offset="100%" stopColor="#94a3b8" />
                </linearGradient>
                <linearGradient id="age30" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#fed7aa" /><stop offset="100%" stopColor="#f97316" />
                </linearGradient>
                <linearGradient id="age60" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#fbcfe8" /><stop offset="100%" stopColor="#ec4899" />
                </linearGradient>
                <linearGradient id="age90" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#fca5a5" /><stop offset="100%" stopColor="#ef4444" />
                </linearGradient>
                <linearGradient id="ageCritical" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#f87171" /><stop offset="100%" stopColor="#b91c1c" />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              
              {/* Customer Names are now along the X Axis */}
              <XAxis 
                dataKey="CustomerName" 
                type="category"
                tick={{ fill: '#0f172a', fontSize: 11, fontWeight: 500 }} 
                axisLine={{ stroke: '#cbd5e1' }}
                tickLine={false}
                interval={0} // Forces Recharts to show EVERY customer name instead of skipping labels
                angle={-25}  // Angled text so names don't crash into each other
                textAnchor="end"
              />

              {/* Currencies/Amounts are along the Y Axis */}
              <YAxis 
                type="number"
                tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} 
                tick={{ fill: '#64748b', fontSize: 12 }} 
                axisLine={false}
                tickLine={false}
                width={50}
              />
              
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                contentStyle={{ 
                  backgroundColor: '#ffffff', 
                  borderRadius: '8px', 
                  border: '1px solid #e2e8f0',
                  color: '#334155',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                }}
                cursor={{ fill: '#f8fafc' }}
              />
              
              <Legend wrapperStyle={{ paddingTop: '10px', fontSize: '13px', fontWeight: 500 }} iconType="circle" iconSize={10} />
              
              {/* Stacked vertical bars configuration */}
              <Bar dataKey="Age_0_30" stackId="aging" fill="url(#ageCurrent)" name="0-30 Days" radius={[0, 0, 0, 0]} maxBarSize={35} />
              <Bar dataKey="Age_31_60" stackId="aging" fill="url(#age30)" name="31-60 Days" radius={[0, 0, 0, 0]} maxBarSize={35} />
              <Bar dataKey="Age_61_90" stackId="aging" fill="url(#age60)" name="61-90 Days" radius={[0, 0, 0, 0]} maxBarSize={35} />
              <Bar dataKey="Age_91_120" stackId="aging" fill="url(#age90)" name="91-120 Days" radius={[0, 0, 0, 0]} maxBarSize={35} />
              <Bar dataKey="Age_Above120" stackId="aging" fill="url(#ageCritical)" name="Over 120 Days" radius={[4, 4, 0, 0]} maxBarSize={35} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}